import { REGEX } from "@common/constants/regex.constant";
import { CapitalMemberType } from "@common/enums/CapitalMemberType";
import { FundType } from "@common/enums/FundType";
import { MoicRoundOff } from "@common/enums/tearsheet/TearsheetState";
import { TransactionIndexSuffixType } from "@common/enums/TransactionIndexSuffixType";
import { ACTIVITY_ALERT_CONFIG, ADD_TASK_CONFIG, EVENT_FREQUENCY, EVENT_TITLE_VALUE } from "@common/constants/alert-event.constant";
import { InvestmentActivity } from "@common/enums/InvestmentActivity";
import { IAlertEvent } from "@common/models/IAlertEvent";

export class Utils {
    /**
     * Removes leading indentation from each line in a string and trims the result.
     * @param str The input string.
     * @returns The string with indentation removed.
     */
    static stripIndent(str: string): string {
        return str.replace(REGEX.LEADING_WHITESPACE_MULTILINE, '').trim();
    }

    /**
     * Checks if a value is null or undefined.
     * @param value The value to check.
     * @returns True if value is null or undefined, false otherwise.
     */
    static isNil(value: any): boolean {
        return value === null || value === undefined || value === 'null' || value === 'undefined';
    }

    /**
     * Deep clones an object using JSON methods.
     * @param obj The object to clone.
     * @returns A deep clone of the object.
     */
    static deepClone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    /**
     * Capitalizes the first letter of a string.
     * @param str The input string.
     * @returns The string with the first letter capitalized.
     */
    static capitalize(str: string): string {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    /**
     * Domain name standardized format
     * @param str The input string.
     * @returns eg. abc.com
     */
    static domainFormat(str: string): string {
        if (!str || ('-0-' === str)) return '';
        try {
            const domain = str.replace(REGEX.STARTS_WITH_HTTP, '').replace(REGEX.STARTS_WITH_WWW, '').split('/');
            return domain.length ? domain[0] : str;
        } catch {
            return str;
        }
    }

    /**
     * Moic Round Off min(0.99) and max(1.01) value check
     * @param value The input any.
     * @returns eg. true/false
     */
    static isCheckMoicValue(value: any): boolean {
        let flag = false;
        if (typeof value === 'number') {
            const valueToDigit = Number(value.toFixed(2));
            flag = (valueToDigit >= MoicRoundOff.min && valueToDigit <= MoicRoundOff.max);
        }

        return flag;
    }

    static compareColumnValues(value1: any, value2: any): number {
        if (value1 == null && value2 != null) return -1;
        if (value1 != null && value2 == null) return 1;
        if (value1 == null && value2 == null) return 0;

        if (typeof value1 === 'string' && typeof value2 === 'string') {
            return value1.localeCompare(value2);
        }

        if (value1 < value2) return -1;
        if (value1 > value2) return 1;
        return 0;
    }

    static isValidEmail(email: string) {
        const regex = REGEX.EMAIL;
        return regex.test(email);
    }
    static urlParams(link: any) {
        const decodeLink = decodeURIComponent(link);
        let queryParams = decodeLink?.split('?')[1];
        let data: any = null;

        if (queryParams) {
            const params = queryParams.split('&');
            data = {};
            let pair = null;
            for (const param of params) {
                pair = param.split('=');
                data[`${pair[0]}`] = pair[1];
            }
        }
        return data;
    }

    // extract unique emails from otherContact
    static extractEmails(company: any): string[] {
        const emails = new Set<string>();
        Object.values(company).forEach(value => {
            // If array → check each item
            if (Array.isArray(value)) {
                value.forEach(item => {
                    if (typeof item === 'string') {
                        emails.add(item.toLowerCase());
                    }
                });
            }

            // If single string → check directly
            if (typeof value === 'string') {
                emails.add(value.toLowerCase());
            }
        });
        return Array.from(emails);
    }

    static setOfficeLocation(officeLocation: string): { street: string, city: string, state: string, country: string } {
        const parts = officeLocation?.split(",").map(x => x.trim()).filter(x => x) ?? [];
        let street = "";
        let city = "";
        let state = "";
        let country = "";

        // If more than 4 parts → pack extra into street
        if (parts.length > 4) {
            const extraCount = parts.length - 3; // keep last 3 as city/state/country
            street = parts.slice(0, extraCount).join(", ");
            city = parts[extraCount] || "";
            state = parts[extraCount + 1] || "";
            country = parts[extraCount + 2] || "";
        } else {
            // Normal mapping
            street = parts[0] || "";
            city = parts[1] || "";
            state = parts[2] || "";
            country = parts[3] || "";
        }
        return { street, city, state, country };
    }

    /**
     * Turn a date into a folder name valid in a POSIX filesystem
     * @param date 
     * @returns a path string representing the date
     */
    static formatDateForPosixFolder(date: Date): string {
        const YYYY = date.getFullYear();
        const MM = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-11, so +1 and pad with 0
        const DD = String(date.getDate()).padStart(2, '0');
        const HH = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        const SS = String(date.getSeconds()).padStart(2, '0');

        return `${YYYY}${MM}${DD}_${HH}${mm}${SS}`;
    }

    /**
   * 
   * Decides priority of fund types (lower number = higher priority)
   */

    static getFundTypePriority(type: string = ''): number {
        const t = type.toLowerCase();

        // Highest priority group
        if (
            t === (FundType.MainFundCoinvestment.toLowerCase())
        ) return 1;

        // Second priority group
        if (
            t === (FundType.PartnersSmas.toLowerCase())
        ) return 2;

        // Third priority
        if (t === (FundType.DirectCoinvestment.toLowerCase())) return 3;

        // Everything else including 'Others' goes last
        return 4;
    }

    /**
     * 
     * Converts Roman numerals (I, II, VI etc.) into numbers (1,2,6)
     */

    static romanToNumber(roman: string | null): number {
        if (!roman) return 999;
        const map: any = { I: 1, V: 5, X: 10, L: 50, C: 100 };
        let num = 0, prev = 0;
        for (let i = roman.length - 1; i >= 0; i--) {
            const curr = map[roman[i]];
            if (curr < prev) num -= curr;
            else num += curr;
            prev = curr;
        }
        return num;
    }

    /**
     * 
     * Breaks fund name into structured parts so we can sort logically
     */

    static parseFund(name: string) {

        // Remove "Fund" word if present
        const clean = name.replace(REGEX.STARTS_WITH_FUND, '').trim();

        // Extract fund group name  (QLCP, QCS etc.)
        const prefix = (clean.match(REGEX.LEADING_ALPHABETS) || [''])[0].toLowerCase();

        // Extract Roman numeral (handles both "VI" and "-VI")
        const romanMatch = clean.match(REGEX.ROMAN_AFTER_SPACE_OR_HYPHEN);
        const roman = this.romanToNumber(romanMatch ? romanMatch[1] : null);

        // Extract instance number like (1), (2), (3)
        const instanceMatch = clean.match(REGEX.NUMBER_IN_PARENTHESES);
        const instance = instanceMatch ? Number(instanceMatch[1]) : 0;

        // Extract suffix like "-C", "-O" (only if after Roman)
        const hyphenMatch = clean.match(REGEX.ROMAN_HYPHEN_WORD_END);
        const hyphen = hyphenMatch ? hyphenMatch[1].toLowerCase() : '';

        // Extract named variants like "Affiliates", "Quest"
        const namedMatch = clean
            .replace(REGEX.NUMBER_IN_PARENTHESES2, '')
            .replace(REGEX.PREFIX_ROMAN_PATTERN, '')
            .trim();

        // Only consider as named if it's not a hyphen type
        const named = (!hyphen && namedMatch) ? namedMatch.toLowerCase() : '';

        // Assign type priority inside same fund
        // 0 -> Base fund (QLCP I)
        // 1 -> Suffix (QLCP I-C)
        // 2 -> Numbered (QLCP I (1))
        // 3 -> Named (QLCP I Affiliates)

        let type = 0;
        if (hyphen) type = 1;      
        else if (instance > 0) type = 2;
        else if (named) type = 3;

        return { prefix, roman, type, hyphen, instance, named };
    }

    /**
     *  
     * Main method to sort the data 
     * Sort by fund type, then fund family (Roman first, then prefixed), then detailed name structure
     * 
     */

    static sortDataByFundOrder(data: any[], fundCapMembers: any[]): any[] {

        if(!fundCapMembers || !Array.isArray(fundCapMembers)){
            return data;
        }
        
        // Create a lookup: Fund name -> Fund type
        const fundMap = new Map(
            fundCapMembers.map(m => [
                m.name.toLowerCase(),
                m.fundType
            ])
        );

        // Create a lookup: Fund name -> Fund family
        const fundFamilyMap = new Map<string, string>(
            fundCapMembers.map(m => [
                m.name.toLowerCase(),
                m.fundFamily
            ])
        );

        const getMappedFamily = (name: string): string => {
            const key = name.toLowerCase();

            if (fundFamilyMap.has(key)) {
                return fundFamilyMap.get(key)!;
            }

            for (const [mapKey, value] of fundFamilyMap.entries()) {
                if (key.includes(mapKey) || mapKey.includes(key)) {
                    return value;
                }
            }

            return name;
        }

        // Classify fundFamily for sorting
        const getFamilyRank = (val: string) => {
            const clean = val.toLowerCase().trim();

            // Pure Roman (V, VI, VII...)
            const isPureRoman = REGEX.ROMAN_NUMERAL.test(clean);

            if (isPureRoman) {
                return {
                    group: 1,
                    prefix: '',
                    roman: this.romanToNumber(clean.toUpperCase())
                };
            }

            // Prefixed (QCO I, QCO II...)
            const prefixMatch = clean.match(REGEX.LOWERCASE_START);
            const romanMatch = clean.match(REGEX.ROMAN_NUMERAL_ROUND_BRACKET);

            return {
                group: 2,
                prefix: prefixMatch ? prefixMatch[0] : '',
                roman: this.romanToNumber(romanMatch ? romanMatch[0].toUpperCase() : null)
            };
        };

        return data.sort((a, b) => {

            // Step 1: Sort by fund type priority   
            const typeA = this.getFundTypePriority(
                (fundMap.get(a.name.toLowerCase()) || '') as string
            );
            const typeB = this.getFundTypePriority(
                (fundMap.get(b.name.toLowerCase()) || '') as string
            );

            // If different fund types -> decide the order here
            if (typeA !== typeB) {
                return typeA - typeB;
            }

            // Step 2: Sort by fund family ( Skip for FundType = Others)
            if (typeA !== 4 && typeB !== 4) {
                const famAKey = (getMappedFamily(a.name));
                const famBKey = (getMappedFamily(b.name));

                const famA = getFamilyRank(famAKey);
                const famB = getFamilyRank(famBKey);

                // 1. Roman-only first
                if (famA.group !== famB.group) {
                    return famA.group - famB.group;
                }

                // 2. Prefix (QCO, QCS...)
                if (famA.prefix !== famB.prefix) {
                    return famA.prefix.localeCompare(famB.prefix);
                }

                // 3. Roman order
                if (famA.roman !== famB.roman) {
                    return famA.roman - famB.roman;
                }
            }

            // Step 3: If same fund type -> sort by fund name structure
            const A = this.parseFund(a.name);
            const B = this.parseFund(b.name);

            return (
                // Group same fund families together (QLCP, QCS, QEP)
                A.prefix.localeCompare(B.prefix) ||

                // Sort by version (I, II, III, ...)
                A.roman - B.roman ||

                // Base -> Numbered -> Named -> Suffix
                A.type - B.type ||

                // Sort (1), (2), (3)
                A.instance - B.instance ||

                // Sort names alphabetically 
                A.named.localeCompare(B.named) ||

                // Sort suffix like -C, -O
                A.hyphen.localeCompare(B.hyphen) ||

                // Final fallback 
                a.name.localeCompare(b.name)
            );
        });
    }

    static standardizeGeography(value?: string | null): string {
        if (!value) return value ?? "";
        return value.replace(/\./g, "");
    }

    static textFieldStatus(text: string) : number {
        if (text.length <= 5) {
            return 0;
        }

        if (text.includes("NEEDS REVIEW")) {
            return 0;
        }

        return 1;
    }

    static getWeekRange(date = new Date()) {
        const current = new Date(date);

        // Get day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
        const day = current.getDay();

        // Calculate Monday
        const diffToMonday = day === 0 ? -6 : 1 - day;

        const startDate = new Date(current);
        startDate.setDate(current.getDate() + diffToMonday);
        startDate.setHours(0, 0, 0, 0);

        // Calculate Sunday
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);

        return { startDate, endDate };
    }

    static getOrdinal(n: number): string {
        const mod10 = n % 10;
        const mod100 = n % 100;

        if (mod10 === 1 && mod100 !== 11) return `${n}st`;
        if (mod10 === 2 && mod100 !== 12) return `${n}nd`;
        if (mod10 === 3 && mod100 !== 13) return `${n}rd`;
        return `${n}th`;
    }

    static assignTransactionIndex(data: any[], suffix: TransactionIndexSuffixType.FUNDING | TransactionIndexSuffixType.PROCEEDS) {
        // Sort unique combinations by funding Date
        const sorted = data.sort((a, b) =>
            new Date(a.fundingDate).getTime() -
            new Date(b.fundingDate).getTime()
        );
        // Get unique (name + date) combinations
        const uniqueMap = new Map<string, any>();
        sorted.forEach(item => {
            const date = item.fundingDate;
            const key = `${item.name}|${date}`;

            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, item);
            }
        });

        // Build lookup
        const lookup = new Map<string, string>();
        sorted.forEach((item, index) => {
            const key = `${item.name}|${item.fundingDate}`;
            lookup.set(key, `${Utils.getOrdinal(index + 1)} ${suffix}`);
        });

        // Return updated array
        return data.map(item => ({
            ...item,
            transactionIndex: lookup.get(`${item.name}|${item.fundingDate}`)
        }));
    }

    static fundFlowSortFunds(funds: string[], memberTypeByGroup?: Record<string, string>): string[] {
      const MEMBER_TYPE_ORDER: Record<string, number> = {
        [CapitalMemberType.MainFund]:           0,
        [CapitalMemberType.Coinvestment]:       1,
        [CapitalMemberType.DirectCoinvestment]: 2,
      };
    
      const parse = (name: string) => {
        const c = name.trim().toUpperCase();
        const iv = c.includes('CI') || c.includes('FOO');
        const bp = c
          .replace('CI + FOO', '').replace('CI+FOO', '')
          .replace(/CI|FOO/g, '').trim();
        const m = bp.match(/FUND\s*(\d+)([A-Z]?)/);
        return m
          ? { num: parseInt(m[1], 10), let: m[2] || '', iv }
          : { num: 9999, let: '', iv };
      };
    
      return [...funds].sort((a, b) => {
        // Primary: numeric fund number
        const A = parse(a), B = parse(b);
        if (A.num !== B.num) return A.num - B.num;
    
        // Secondary: memberType priority within the same number bucket
        if (memberTypeByGroup) {
          const mtA = memberTypeByGroup[a] ?? '';
          const mtB = memberTypeByGroup[b] ?? '';
          const priA = MEMBER_TYPE_ORDER[mtA] ?? 3;
          const priB = MEMBER_TYPE_ORDER[mtB] ?? 3;
          if (priA !== priB) return priA - priB;
        }
    
        // Tertiary: letter suffix, then ci/foo variants last
        if (A.let !== B.let) return A.let.localeCompare(B.let);
        if (A.iv !== B.iv) return A.iv ? 1 : -1;
        return 0;
      });
    }

    /**
     * Returns the fiscal quarter start as a local-midnight Date.
     * Fiscal quarters: Q1=Apr, Q2=Jul, Q3=Oct, Q4=Jan (next calendar year).
     */
    static getFiscalQuarterStartDate(year: number, quarter: number): Date {
        const fiscalQStartMonths = [3, 6, 9, 0]; // 0-based months
        const month = fiscalQStartMonths[quarter - 1];
        const qYear = quarter === 4 ? year + 1 : year;
        return new Date(qYear, month, 1);
    }

    /**
     * Normalizes a mixed CalendarItem / AlertEvent body into a clean IAlertEvent shape.
     * Maps frontend field names (teamAssignment, quarter, startDate/endDate) to their
     * IAlertEvent equivalents and fills required fields with sensible defaults.
     */
    static normalizeAlertEvent(body: any): IAlertEvent {
        const DAY_MS = 1000 * 60 * 60 * 24;
        const toLocalMidnight = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const startMs = body.startDate ? toLocalMidnight(new Date(body.startDate)) : 0;
        const endMs = body.endDate ? toLocalMidnight(new Date(body.endDate)) : startMs;
        const derivedEndDays = Math.max(0, Math.round((endMs - startMs) / DAY_MS));

        let derivedStartDateCount = body.startDateCount ?? 0;
        if (body.startDate && body.quarter) {
            const match = body.quarter.match(/^(\d{4})-Q([1-4])$/);
            if (match) {
                const quarterYear = Number(match[1]);
                const quarterNumber = Number(match[2]);
                const quarterStart = Utils.getFiscalQuarterStartDate(quarterYear, quarterNumber);
                const quarterStartMs = toLocalMidnight(quarterStart);
                const diffDays = Math.round((startMs - quarterStartMs) / DAY_MS);
                // Quarter start = 1
                derivedStartDateCount = diffDays + 1;
            }
        }

        const activityConfig = ACTIVITY_ALERT_CONFIG[body.eventType as InvestmentActivity];
        const taskConfig = (body.eventType === InvestmentActivity.Others && body.association)
            ? (ACTIVITY_ALERT_CONFIG[body.association as InvestmentActivity]?.taskConfig ?? null)
            : body.taskConfig ?? activityConfig?.taskConfig ?? null;
        const addConfig = ADD_TASK_CONFIG[body.eventType as InvestmentActivity];
        const prepareTitle = (body.title ?? 'TASK').toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
        const prepareEventType = (body.eventType ?? 'EVENTTYPE').toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
        const prepareValue = `${prepareTitle}_${prepareEventType}_${Date.now()}`;

        return {
            ...(body._id ? { _id: body._id } : {}),
            title: body.title,
            description: body.description ?? '',
            value: prepareValue,
            frequency: body.frequency ?? null,
            eventType: body.eventType,
            // Calculated from quarter start
            startDateCount: derivedStartDateCount,
            // Calculated from startDate -> endDate
            endDaysCount: body.endDaysCount ?? derivedEndDays,
            teams: body.teams ?? body.teamAssignment ?? [],
            alert: body.alert ?? null,
            canTrackCompletion: body.canTrackCompletion ?? addConfig?.canTrackCompletion ?? false,
            showStatus: body.showStatus ?? addConfig?.showStatus ?? false,
            isAllTeamsSelected: body.isAllTeamsSelected ?? false,
            isActive: body.isActive ?? true,
            portfolioCompanies: body.portfolioCompanies ?? [],
            taskConfig,
            association: body.association ?? null
        };
    }

    /**
     * Parses a stored Remaining Term value from the "Xm Yd" storage format.
     * Returns null if the value does not match the expected format.
     */
    static parseRemainingTermValue(value: string): { months: number; days: number } | null {
        if (!value || typeof value !== 'string') return null;
        const match = value.match(/^(\d+)m\s*(\d+)d$/i);
        if (!match) return null;
        return { months: parseInt(match[1], 10), days: parseInt(match[2], 10) };
    }

    /**
     * Converts months and days into a human-readable decimal years string.
     *
     * Rules:
     *   - months = 0, days > 0  → "~0.1 Years"  (days-only: approximate minimum)
     *   - months = 0, days = 0  → "0.0 Years"
     *   - months > 0 (any days) → "X.X Years"   (no tilde; days do not affect the number)
     */
    static formatRemainingTerm(months: number, days: number): string {
        if (months === 0) {
            return days > 0 ? '~0.1 Years' : '0.0 Years';
        }
        const decimal = Math.round((months / 12) * 10) / 10;
        return `${decimal.toFixed(1)} Years`;
    }

    /**
     * Parses a stored "Xm Yd" Remaining Term value and returns the display string.
     * Falls back to the raw string for unrecognised formats, and "N/A" for null/empty inputs.
     */
    static getRemainingTermDisplay(value: any): string {
        const NULL_DISPLAY = 'N/A';
        if (value === null || value === undefined || value === '' || value === NULL_DISPLAY) {
            return NULL_DISPLAY;
        }
        if (typeof value === 'string') {
            const parsed = Utils.parseRemainingTermValue(value);
            if (parsed) {
                return Utils.formatRemainingTerm(parsed.months, parsed.days);
            }
            return value || NULL_DISPLAY;
        }
        return String(value);
    }

}